import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { Rate } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import NoImage from "../../../images/no-image.jpg"; // replace with your placeholder image
import { createBank, getBankById, updateBank } from "../../../services/BankService";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getCurrency } from "../../../services/CurrencyService";
import { getCountries } from "../../../services/CountryService";
import Swal from "sweetalert2";
import PageTitle from "../../layouts/PageTitle";
import { Button } from "react-bootstrap";
import CkEditorBlog from "../Forms/CkEditor/CkEditorBlog";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import CustomClassicEditor from "../CustomClassicEditor";

const AddBank = () => {
  const [formData, setFormData] = useState({
    bank_name: "",
    bank_highlight: "",
    bank_availability: "",
    bank_level: "",
    bank_fees: "",
    bank_fees_desc: "",
    bank_rating: 0,
    bank_rating_link: "",
    bank_open_account_link: false,
    bank_info_link: false,
    bank_open_account_linkurl: "",
    bank_info_linkurl: "",
    currency: null,
    country: null,
  });

  const [highlightImage, setHighlightImage] = useState(null);
  const [existingHighlightImage, setExistingHighlightImage] = useState("");

  const [bankLogo, setBankLogo] = useState(null);
  const [existingLogo, setExistingLogo] = useState("");
  const [errors, setErrors] = useState({});
  const hasFetched = useRef(false);
  const hasDataFetched = useRef(false);
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState([
    {
      name: "",
      description: "",
      fields: [{ key: "", value: "" }],
    },
  ]);

  const navigate = useNavigate();
  const location = useLocation();

  // dropdown mock data
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [currencyPage, setCurrencyPage] = useState(1);
  const [currencyTotalPages, setCurrencyTotalPages] = useState(1);
  const [currencyLoading, setCurrencyLoading] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]);
  const [countryPage, setCountryPage] = useState(1);
  const [countryTotalPages, setCountryTotalPages] = useState(1);
  const [countryLoading, setCountryLoading] = useState(false);

  const handleHighlightImageChange = (e) => {
    setHighlightImage(e.target.files[0]);
  };

  const fetchCurrency = async (pageNumber = 1, query = "") => {
    if (pageNumber > currencyTotalPages) return; // no more pages
    try {
      setCurrencyLoading(true);
      const limit = 5; // set per-page limit
      const response = await getCurrency(pageNumber, limit, query);
      const { data, pagination } = response.data;

      const options = (data || []).map((c) => ({ value: c.id, label: `${c.currency_value}(${c.currency_symbol})` }));

      if (pageNumber === 1) {
        // first page or search => replace
        setCurrencyOptions(options);
      } else {
        // append for scroll
        setCurrencyOptions((prev) => [...prev, ...options]);
      }

      setCurrencyPage(pagination?.page || pageNumber);
      setCurrencyTotalPages(pagination?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching currency:", error);
    } finally {
      setCurrencyLoading(false);
    }
  };

  const fetchCountry = async (pageNumber = 1, query = "") => {
    if (pageNumber > countryTotalPages) return; // no more pages
    try {
      setCountryLoading(true);
      const limit = 5; // set per-page limit
      const response = await getCountries(pageNumber, limit, query);
      const { data, pagination } = response.data;

      const options = (data || []).map((c) => ({ value: c.id, label: c.name }));

      if (pageNumber === 1) {
        // first page or search => replace
        setCountryOptions(options);
      } else {
        // append for scroll
        setCountryOptions((prev) => [...prev, ...options]);
      }

      setCountryPage(pagination?.page || pageNumber);
      setCountryTotalPages(pagination?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching countries:", error);
    } finally {
      setCountryLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleFileChange = (e) => {
    setBankLogo(e.target.files[0]);
  };

  const handleRatingChange = (value) => {
    setFormData({ ...formData, bank_rating: value });
  };

  const handleAddPackage = () => {
    setPackages([
      ...packages,
      { name: "", description: "", fields: [{ key: "", value: "" }] },
    ]);
  };

  const handleRemovePackage = (index) => {
    const newPackages = packages.filter((_, i) => i !== index);
    setPackages(newPackages);
  };

  const handlePackageChange = (index, field, value) => {
    const newPackages = [...packages];
    newPackages[index][field] = value;
    setPackages(newPackages);
  };

  const handleAddField = (pkgIndex) => {
    const newPackages = [...packages];
    newPackages[pkgIndex].fields.push({ key: "", value: "" });
    setPackages(newPackages);
  };

  const handleRemoveField = (pkgIndex, fieldIndex) => {
    const newPackages = [...packages];
    newPackages[pkgIndex].fields.splice(fieldIndex, 1);
    setPackages(newPackages);
  };

  const handleFieldChange = (pkgIndex, fieldIndex, key, value) => {
    const newPackages = [...packages];
    newPackages[pkgIndex].fields[fieldIndex][key] = value;
    setPackages(newPackages);
  };

  const cleanEditorContent = (html = "") => {
    if (!html) return "";

    let cleaned = html.trim();

    // loop because last may be [empty_line] + <p>&nbsp;</p> + [empty_line]
    let changed = true;

    while (changed) {
      changed = false;

      // remove only one trailing empty paragraph block
      const removedParagraph = cleaned.replace(
        /<p>(?:&nbsp;|\s|<br\s*\/?>)*<\/p>\s*$/i,
        ""
      );

      if (removedParagraph !== cleaned) {
        cleaned = removedParagraph.trim();
        changed = true;
      }

      // remove only one trailing [empty_line]
      const removedToken = cleaned.replace(
        /\[empty_line\]\s*$/i,
        ""
      );

      if (removedToken !== cleaned) {
        cleaned = removedToken.trim();
        changed = true;
      }
    }

    return cleaned;
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.bank_name.trim()) newErrors.bank_name = "Bank name is required";
    if (!formData.bank_fees) newErrors.bank_fees = "Bank fees are required";
    if (!formData.bank_fees_desc.trim())
      newErrors.bank_fees_desc = "Fees description is required";
    // if (!formData.currency) newErrors.currency = "Currency is required";
    if (!formData.country) newErrors.country = "Country is required";

    // Conditional validation for URLs
    if (formData.bank_open_account_link && !formData.bank_open_account_linkurl.trim()) {
      newErrors.bank_open_account_linkurl = "Open Account Link URL is required";
    }
    if (formData.bank_info_link && !formData.bank_info_linkurl.trim()) {
      newErrors.bank_info_linkurl = "Info Link URL is required";
    }

    return newErrors;
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        // 1️⃣ Fetch dropdowns first
        const [countryRes] = await Promise.all([
          // getCurrency(1, 100), // fetch all or enough for dropdown
          getCountries(1, 5000),
        ]);

        // const currencyOpts = (currencyRes.data?.data || []).map((c) => ({
        //   value: c.id,
        //   label: `${c.currency_value} (${c.currency_symbol})`,
        // }));
        // setCurrencyOptions(currencyOpts);

        const countryOpts = (countryRes.data?.data || []).map((c) => ({
          value: c.id,
          label: c.name,
        }));
        setCountryOptions(countryOpts);

        // 2️⃣ Then fetch bank by ID
        if (id) {
          const res = await getBankById(id);
          if (res.data?.success) {
            const bank = res.data.data;
            setFormData({
              bank_name: bank.bank_name || "",
              bank_highlight: bank.bank_highlight || "",
              bank_availability: bank.bank_availability || "",
              bank_level: bank.bank_level || "",
              bank_fees: bank.bank_fees || "",
              bank_fees_desc: bank.bank_fees_desc || "",
              bank_rating: bank.bank_rating || 0,
              bank_rating_link: bank.bank_rating_link || "",
              bank_open_account_link: bank.bank_open_account_link == 1 ? true : false,
              bank_info_link: bank.bank_info_link == 1 ? true : false,
              bank_open_account_linkurl: bank.bank_open_account_linkurl || "",
              bank_info_linkurl: bank.bank_info_linkurl || "",
              // currency: currencyOpts.find((c) => c.value === bank.currency_id) || null,
              country: countryOpts.find((c) => c.value === bank.country_id) || null,
            });
            setPackages(bank.bank_packages?.length ? bank.bank_packages : packages);
            setExistingLogo(bank.bank_logo_url || "");
            setExistingHighlightImage(bank.bank_highlight_image_url || "");
          } else {
            console.error("Failed to fetch bank by ID");
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchData();
    }
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const fd = new FormData();
      if (bankLogo) fd.append("bank_logo", bankLogo);

      if (highlightImage) {
        fd.append("bank_highlight_image", highlightImage);
      }

      // append normal fields
      const submitData = {
        ...formData,
        bank_highlight: cleanEditorContent(formData.bank_highlight),
        bank_open_account_linkurl: formData.bank_open_account_link
          ? formData.bank_open_account_linkurl
          : "",
        bank_info_linkurl: formData.bank_info_link
          ? formData.bank_info_linkurl
          : "",
        bank_open_account_link: !!formData.bank_open_account_link,
        bank_info_link: !!formData.bank_info_link,
      };

      // append normal fields
      Object.keys(submitData).forEach((key) => {
        if (key === "country") {
          fd.append(`${key}_id`, submitData[key]?.value || "");
        } else {
          fd.append(key, submitData[key]);
        }
      });

      // append packages as JSON
      fd.append("bank_packages", JSON.stringify(packages));

      if (id) {
        const response = await updateBank(id, fd);
        if (response.data?.success) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: response.data?.message || "Bank details updated successfully!",
          });
          navigate("/bank", {
            state: {
              page: location.state?.page || 1,
              searchTerm: location.state?.searchTerm || "",
            },
          });
        } else {
          console.error(
            "Error updating bank:",
            response?.data?.message || "Unknown error"
          );
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response?.data?.message || "Failed to update bank",
          });
        }
      } else {
        const response = await createBank(fd);
        if (response.data?.success) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: response.data?.message || "Bank details added successfully",
          });
          navigate("/bank", {
            state: {
              page: location.state?.page || 1,
              searchTerm: location.state?.searchTerm || "",
            },
          });
        }
        else {
          console.error(
            "Error updating bank:",
            response.data?.message || "Unknown error"
          );
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data?.message || "Failed to added bank",
          });
        }
      }
    } catch (err) {
      console.error(
        "Error while adding or updating bank:",
        err || "Unknown error"
      );
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err || "Failed to adding or updating bank",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div id="preloader">
        <div className="sk-three-bounce">
          <div className="sk-child sk-bounce1"></div>
          <div className="sk-child sk-bounce2"></div>
          <div className="sk-child sk-bounce3"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageTitle
        activeMenu={`${id ? "Edit Bank" : "Add Bank"}`}
        motherMenu="Bank"
        activeMenuLink={`${id ? `edit-bank/${id}` : "add-bank"}`}
        motherMenuLink={"bank"}
      />
      <div className="row">
        <div className="col-xl-12">
          <div className="row">
            <div className="col-xl-12 col-xxl-12">
              <div className="card h-auto">
                <div className="card-body">
                  <form onSubmit={handleSubmit} encType="multipart/form-data">
                    {/* Bank Logo */}
                    <div
                      onClick={() => {
                        navigate("/bank", {
                          state: {
                            page: location.state?.page || 1,
                            searchTerm: location.state?.searchTerm || "",
                          },
                        });
                      }}
                      className="mb-4 d-flex align-items-center flex-wrap gap-2 cursor-pointer w-max"
                    >
                      <i className="fas fa-chevron-left"></i>
                      <div>Bank List</div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Bank Logo</label>
                      <div>
                        <img
                          src={bankLogo ? URL.createObjectURL(bankLogo) : existingLogo || NoImage}
                          alt="Bank Logo"
                          width={100}
                          height={100}
                          className="rounded border mb-2"
                        />
                      </div>
                      <div className="change-btn d-flex align-items-center flex-wrap">
                        <input
                          type="file"
                          onChange={handleFileChange}
                          id="imageUpload"
                          className="d-none"
                        />
                        <label
                          htmlFor="imageUpload"
                          className="btn btn-primary ms-0"
                        >
                          Select Image
                        </label>
                      </div>
                    </div>

                    {/* Bank Name */}
                    <div className="mb-3">
                      <label className="form-label">Bank Name</label>
                      <input
                        type="text"
                        name="bank_name"
                        className={`form-control ${errors.bank_name ? "is-invalid" : ""}`}
                        value={formData.bank_name}
                        onChange={handleChange}
                        placeholder="Enter bank name"
                      />
                      {errors.bank_name && <div className="invalid-feedback">{errors.bank_name}</div>}
                    </div>

                    {/* Other details */}
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Bank Fees</label>
                        <input
                          type="text"
                          name="bank_fees"
                          className={`form-control ${errors.bank_fees ? "is-invalid" : ""}`}
                          value={formData.bank_fees}
                          onChange={handleChange}
                          placeholder="Enter bank fees"
                        />
                        {errors.bank_fees && <div className="invalid-feedback">{errors.bank_fees}</div>}
                      </div>

                      <div className="col-md-6 mb-3 d-flex flex-column">
                        <label className="form-label">Rating</label>
                        <input
                          type="text"
                          name="bank_rating"
                          className={`form-control ${errors.bank_rating ? "is-invalid" : ""}`}
                          value={formData.bank_rating}
                          onChange={handleChange}
                          placeholder="Enter bank rating"
                        />
                        {/* <Rate allowHalf value={formData.bank_rating} onChange={handleRatingChange} /> */}
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-12 col-6 mb-3">
                        <label className="form-label">Bank Rating Link</label>
                        <input
                          type="text"
                          name="bank_rating_link"
                          className={`form-control ${errors.bank_rating_link ? "is-invalid" : ""}`}
                          value={formData.bank_rating_link}
                          onChange={handleChange}
                          placeholder="Enter bank rating link"
                        />
                        {errors.bank_rating_link && <div className="invalid-feedback">{errors.bank_rating_link}</div>}
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Fees Description</label>
                      <textarea
                        name="bank_fees_desc"
                        className={`form-control ${errors.bank_fees_desc ? "is-invalid" : ""}`}
                        value={formData.bank_fees_desc}
                        onChange={handleChange}
                        placeholder="Describe fee details..."
                        rows={3}
                      ></textarea>
                      {errors.bank_fees_desc && (
                        <div className="invalid-feedback">{errors.bank_fees_desc}</div>
                      )}
                    </div>

                    {/* Packages Section */}
                    {/* <div className="mb-4">
                      <label className="form-label">Bank Packages</label>
                      {packages.map((pkg, pkgIndex) => (
                        <div key={pkgIndex} className="border rounded p-3 mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0">Package {pkgIndex + 1}</h6>
                            {packages.length > 1 && (
                              <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={() => handleRemovePackage(pkgIndex)}
                              >
                                Remove
                              </button>
                            )}
                          </div>

                          <div className="mb-2">
                            <input
                              type="text"
                              className="form-control mb-2"
                              placeholder="Package Name"
                              value={pkg.name}
                              onChange={(e) =>
                                handlePackageChange(pkgIndex, "name", e.target.value)
                              }
                            />
                            <textarea
                              className="form-control"
                              rows={2}
                              placeholder="Package Description"
                              value={pkg.description}
                              onChange={(e) =>
                                handlePackageChange(pkgIndex, "description", e.target.value)
                              }
                            />
                          </div>

                          <label className="form-label">Custom Fields</label>
                          {pkg.fields.map((field, fieldIndex) => (
                            <div key={fieldIndex} className="d-flex gap-2 mb-2">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Key"
                                value={field.key}
                                onChange={(e) =>
                                  handleFieldChange(pkgIndex, fieldIndex, "key", e.target.value)
                                }
                              />
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Value"
                                value={field.value}
                                onChange={(e) =>
                                  handleFieldChange(pkgIndex, fieldIndex, "value", e.target.value)
                                }
                              />
                              {pkg.fields.length > 1 && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleRemoveField(pkgIndex, fieldIndex)}
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleAddField(pkgIndex)}
                          >
                            + Add Field
                          </button>
                        </div>
                      ))}

                      <button
                        type="button"
                        className="btn btn-outline-success"
                        onClick={handleAddPackage}
                      >
                        + Add Another Package
                      </button>
                    </div> */}

                    <div className="mb-3">
                      <label className="form-label">Bank Description / Package Description</label>

                      <div className="custom-ekeditor cms-radius add-content-ckeditor mb-3">
                        <CKEditor
                          editor={CustomClassicEditor}
                          data={(formData.bank_highlight).replace(/\[empty_line\]/g, '<div data-empty-line="true"></div>') || ""}
                          onChange={(event, editor) => {
                            setFormData({
                              ...formData,
                              bank_highlight: editor.getData(),
                            });
                          }}
                        />
                        {/* <CkEditorBlog
                          value={formData.bank_highlight}
                          onChange={(val) =>
                            setFormData({ ...formData, bank_highlight: val })
                          }
                        /> */}
                      </div>
                    </div>

                    {/* Highlight Image */}
                    <div className="mb-3">
                      <label className="form-label">Package Image</label>

                      <div>
                        <img
                          src={
                            highlightImage
                              ? URL.createObjectURL(highlightImage)
                              : existingHighlightImage || NoImage
                          }
                          alt="Highlight"
                          width={200}
                          height={120}
                          className="rounded border mb-2"
                          style={{ objectFit: "cover" }}
                        />
                      </div>

                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleHighlightImageChange}
                        className="form-control"
                      />
                    </div>

                    {/* Open Account Link Checkbox + URL */}
                    <div className="mb-3">
                      <div className="form-check mb-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="bank_open_account_link"
                          checked={formData.bank_open_account_link}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              bank_open_account_link: e.target.checked,
                              bank_open_account_linkurl: e.target.checked
                                ? formData.bank_open_account_linkurl
                                : "",
                            })
                          }
                        />
                        <label className="form-check-label" htmlFor="bank_open_account_link">
                          Has Open Account Link
                        </label>
                      </div>

                      {/* Open Account Link URL */}
                      {formData.bank_open_account_link && (
                        <>
                          <input
                            type="text"
                            name="bank_open_account_linkurl"
                            className={`form-control ${errors.bank_open_account_linkurl ? "is-invalid" : ""}`}
                            value={formData.bank_open_account_linkurl}
                            onChange={handleChange}
                            placeholder="Enter open account link URL"
                          />
                          {errors.bank_open_account_linkurl && (
                            <div className="invalid-feedback">{errors.bank_open_account_linkurl}</div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Info Link Checkbox + URL */}
                    <div className="mb-3">
                      <div className="form-check mb-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="bank_info_link"
                          checked={formData.bank_info_link}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              bank_info_link: e.target.checked,
                              bank_info_linkurl: e.target.checked
                                ? formData.bank_info_linkurl
                                : "",
                            })
                          }
                        />
                        <label className="form-check-label" htmlFor="bank_info_link">
                          Has Info Link
                        </label>
                      </div>

                      {/* Info Link URL */}
                      {formData.bank_info_link && (
                        <>
                          <input
                            type="text"
                            name="bank_info_linkurl"
                            className={`form-control ${errors.bank_info_linkurl ? "is-invalid" : ""}`}
                            value={formData.bank_info_linkurl}
                            onChange={handleChange}
                            placeholder="Enter info link URL"
                          />
                          {errors.bank_info_linkurl && (
                            <div className="invalid-feedback">{errors.bank_info_linkurl}</div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Currency & Country */}
                    <div className="row">
                      {/* <div className="col-md-6 mb-3">
                        <label className="form-label">Currency</label>
                        <Select
                          value={formData.currency}
                          onChange={(value) => handleSelectChange("currency", value)}
                          options={currencyOptions}
                          placeholder="Select currency"
                          closeMenuOnSelect={true}
                          menuPlacement="auto"
                          menuPosition="fixed"
                          onMenuScrollToBottom={() => fetchCurrency(currencyPage + 1)} // load next page
                          // onInputChange={(inputValue) => fetchCurrency(1, inputValue)} // search resets page
                          isLoading={currencyLoading}
                        />
                        {errors.currency && <div className="text-danger mt-1">{errors.currency}</div>}
                      </div> */}
                      <div className="col-md-12 mb-3">
                        <label className="form-label">Country</label>
                        <Select
                          value={formData.country}
                          onChange={(value) => handleSelectChange("country", value)}
                          options={countryOptions}
                          placeholder="Select country"
                          closeMenuOnSelect={true}
                          menuPlacement="auto"
                          menuPosition="fixed"
                          onMenuScrollToBottom={() => fetchCountry(countryPage + 1)} // load next page
                          // onInputChange={(inputValue) => fetchCountry(1, inputValue)} // search resets page
                          isLoading={countryLoading}
                        />
                        {errors.country && <div className="text-danger mt-1">{errors.country}</div>}
                      </div>
                    </div>

                    <hr style={{ margin: "0px" }} className="mx-2" />
                    <div className="card-footer border-0 text-end py-3 d-flex justify-content-end gap-2">
                      <Button
                        type="button"
                        variant="secondary"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => {
                          navigate("/bank", {
                            state: {
                              page: location.state?.page || 1,
                              searchTerm: location.state?.searchTerm || "",
                            }
                          })
                        }}
                      >
                        Back
                      </Button>

                      {/* Save / Update Button */}
                      <Button
                        type="submit"
                        className="btn btn-primary btn-sm"
                      >
                        {id ? "Update" : "Save"}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddBank;
